import { Box, Stack, Typography } from '@mui/material'
import { autorun, toJS } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react-lite'
import { useEffect } from 'react'
import StoreAchievement from '../StoreAchievement'
import { getNativeName } from 'Helpers/GlobalHelpers'
import StoreTranslate from 'GlobalStores/Cores/Translator/StoreTranslate'
import { useTranslate } from 'GlobalStores/Cores/Translator/useTranslate'
import { useNavigate } from 'react-router-dom'
import Leg3Hc from './Leg3Hc'

const Legs = observer(() => {
    const navigate = useNavigate()
    const translate = useTranslate()
    const localObserver = useLocalObservable(() => ({
        leg1: null,
        leg2: null,
        leg3: null
    }))

    useEffect(() => {
        const disposer = autorun(() => {
            const achievements = StoreAchievement.getAchievements()[StoreAchievement.getSelectedPeriod()]

            if (achievements) {
                const leg1 = achievements.metricsDetails.ov_leg1
                const leg2 = achievements.metricsDetails.ov_leg2
                const leg3 = achievements.metricsDetails.ov_leg3hc

                achievements.value?.forEach(item => {
                    if (item.achievement.rankShort === StoreAchievement.getSelectedRank()) {
                        const leg1value = item.metrics.find(metric => metric.name === 'ov_leg1')
                        if (leg1value) {
                            const customerName1 = leg1?.customers[0]?.customer.humanName
                            localObserver.leg1 = {
                                customerName: {
                                    english: customerName1?.fullName,
                                    native: getNativeName(customerName1, 'nativeName')
                                },
                                id: leg1?.customers[0]?.customer.id.unicity,
                                value: leg1value.value.toLocaleString(),
                                requiredValue: leg1value.requiredValue.toLocaleString()
                            }
                        } else {
                            localObserver.leg1 = null
                        }

                        const leg2value = item.metrics.find(metric => metric.name === 'ov_leg2')
                        if (leg2value) {
                            const customerName2 = leg2?.customers[0]?.customer.humanName
                            localObserver.leg2 = {
                                customerName: {
                                    english: customerName2?.fullName,
                                    native: getNativeName(customerName2, 'nativeName')
                                },
                                id: leg2?.customers[0]?.customer.id.unicity,
                                value: leg2value.value.toLocaleString(),
                                requiredValue: leg2value.requiredValue.toLocaleString()
                            }
                        } else {
                            localObserver.leg2 = null
                        }

                        const leg3value = item.metrics.find(metric => metric.name === 'ov_leg3hc_pv_excluded')
                        if (leg3value) {
                            localObserver.leg3 = {
                                totalCustomers: leg3?.totalCustomers,
                                value: leg3value.value.toLocaleString(),
                                requiredValue: leg3value.requiredValue.toLocaleString()
                            }
                        } else {
                            localObserver.leg3 = null
                        }
                    }
                })
            }
        })

        return () => {
            disposer()
        }
    }, [])

    return localObserver.leg1 || localObserver.leg2 || localObserver.leg3 ? (
        <Stack>
            {localObserver.leg1 && (
                <LegsItem
                    title={
                        <Box onClick={() => navigate(`/user/genealogy/${localObserver.leg1?.id}`)} sx={{ cursor: 'pointer' }}>
                            <Typography variant="h6" fontFamily={'Inter'} fontSize={'14px'} fontWeight={400} color="#22AAEC">
                                Leg 1
                            </Typography>
                        </Box>
                    }
                    subtitle={StoreTranslate.UseLanguage(localObserver.leg1?.customerName)}
                    value={localObserver.leg1?.value}
                    requiredValue={localObserver.leg1?.requiredValue}
                />
            )}
            {localObserver.leg2 && (
                <LegsItem
                    title={
                        <Box onClick={() => navigate(`/user/genealogy/${localObserver.leg2?.id}`)} sx={{ cursor: 'pointer' }}>
                            <Typography variant="h6" fontFamily={'Inter'} fontSize={'14px'} fontWeight={400} color="#22AAEC">
                                Leg 2
                            </Typography>
                        </Box>
                    }
                    subtitle={StoreTranslate.UseLanguage(localObserver.leg2?.customerName)}
                    value={localObserver.leg2?.value}
                    requiredValue={localObserver.leg2?.requiredValue}
                />
            )}
            {localObserver.leg3 && (
                <LegsItem
                    title={<Leg3Hc />}
                    subtitle={`${translate('has')} ${localObserver.leg3?.totalCustomers} ${localObserver.leg3?.totalCustomers > 1 ? 'IDs' : 'ID'}`}
                    value={localObserver.leg3?.value}
                    requiredValue={localObserver.leg3?.requiredValue}
                />
            )}
        </Stack>
    ) : null
})

const LegsItem = ({ title, subtitle, value, requiredValue }) => (
    <Stack direction={'row'} gap={'12px'} justifyContent={'space-between'}>
        <Stack direction={'row'} whiteSpace={'nowrap'} overflow={'hidden'} pr="12px">
            {title}
            {subtitle && (
                <>
                    &nbsp;
                    <Typography variant="h6" fontFamily={'Inter'} fontSize={'14px'} fontWeight={400} color="#9EA9B9">
                        {subtitle}
                    </Typography>
                </>
            )}
        </Stack>
        <Stack alignItems={'center'} justifyContent={'center'} direction={'row'} whiteSpace={'nowrap'}>
            <Typography variant="h6" fontFamily={'Inter'} fontSize={'14px'} fontWeight={400} color="#000000">
                {value}
            </Typography>
            {requiredValue && (
                <>
                    &nbsp;
                    <Typography variant="h6" fontFamily={'Inter'} fontSize={'14px'} fontWeight={400} color="#9EA9B9">
                        / {requiredValue.toLocaleString()}
                    </Typography>
                </>
            )}
        </Stack>
    </Stack>
)

export default Legs
